import React, { useEffect, useState } from "react";
import useAxios from "../../Auth/useAxios";
import Swal from "sweetalert2";
import {
  TrashIcon,
  PencilIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

// Duplicates Modal Component with Delete Options
const DuplicatesModal = ({ duplicates, onClose, onDelete, onMerge }) => {
  // Group duplicates by mobile number
  const groupedDuplicates = duplicates.reduce((groups, lead) => {
    const mobile = lead.mobile;
    if (!groups[mobile]) {
      groups[mobile] = [];
    }
    groups[mobile].push(lead);
    return groups;
  }, {});

  // State for selecting which leads to delete (keep one, delete others)
  const [selectedLeadsToDelete, setSelectedLeadsToDelete] = useState({});
  const [keepLeadForMobile, setKeepLeadForMobile] = useState({});

  // Initialize selection state
  useEffect(() => {
    const newSelectedLeadsToDelete = {};
    const newKeepLeadForMobile = {};

    Object.entries(groupedDuplicates).forEach(([mobile, leads]) => {
      // By default, keep the first lead (oldest or newest based on your preference)
      if (leads.length > 0) {
        newKeepLeadForMobile[mobile] = leads[0].id;
        // Select all other leads for deletion by default
        newSelectedLeadsToDelete[mobile] = leads
          .filter((lead) => lead.id !== leads[0].id)
          .map((lead) => lead.id);
      }
    });

    setSelectedLeadsToDelete(newSelectedLeadsToDelete);
    setKeepLeadForMobile(newKeepLeadForMobile);
  }, [groupedDuplicates]);

  const handleKeepLeadChange = (mobile, leadId) => {
    setKeepLeadForMobile({
      ...keepLeadForMobile,
      [mobile]: leadId,
    });

    // Update selected leads to delete for this mobile
    const leads = groupedDuplicates[mobile];
    const newSelectedLeads = leads
      .filter((lead) => lead.id !== leadId)
      .map((lead) => lead.id);

    setSelectedLeadsToDelete({
      ...selectedLeadsToDelete,
      [mobile]: newSelectedLeads,
    });
  };

  const handleSelectLeadForDeletion = (mobile, leadId, checked) => {
    const currentSelected = selectedLeadsToDelete[mobile] || [];
    let newSelected;

    if (checked) {
      newSelected = [...currentSelected, leadId];
    } else {
      newSelected = currentSelected.filter((id) => id !== leadId);
    }

    setSelectedLeadsToDelete({
      ...selectedLeadsToDelete,
      [mobile]: newSelected,
    });
  };

  const handleDeleteSelected = async () => {
    // Flatten all selected leads to delete
    const allSelectedToDelete = Object.values(selectedLeadsToDelete).flat();

    if (allSelectedToDelete.length === 0) {
      Swal.fire("Error", "Please select at least one lead to delete", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${allSelectedToDelete.length} duplicate lead(s). This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: `Yes, delete ${allSelectedToDelete.length} lead(s)`,
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await onDelete(allSelectedToDelete);
      onClose();
    } catch (error) {
      Swal.fire("Error", "Failed to delete leads", "error");
    }
  };

  const handleMergeSelected = async () => {
    // For each mobile, merge selected leads into the kept lead
    const mergeOperations = [];

    Object.entries(groupedDuplicates).forEach(([mobile, leads]) => {
      const keepLeadId = keepLeadForMobile[mobile];
      const deleteLeadIds = selectedLeadsToDelete[mobile] || [];

      if (deleteLeadIds.length > 0 && keepLeadId) {
        mergeOperations.push({
          leadIds: deleteLeadIds,
          mergeIntoLeadId: keepLeadId,
        });
      }
    });

    if (mergeOperations.length === 0) {
      Swal.fire("Error", "Please select leads to merge", "error");
      return;
    }

    const totalLeadsToMerge = mergeOperations.reduce(
      (sum, op) => sum + op.leadIds.length,
      0
    );

    const result = await Swal.fire({
      title: "Merge Leads?",
      html: `
        <p>You are about to merge <strong>${totalLeadsToMerge}</strong> lead(s).</p>
        <p class="text-sm text-gray-600 mt-2">This will combine data from selected leads into the kept lead and delete the others.</p>
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Merge ${totalLeadsToMerge} lead(s)`,
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      // Perform merge operations
      for (const operation of mergeOperations) {
        await onMerge(operation.leadIds, operation.mergeIntoLeadId);
      }
      onClose();
    } catch (error) {
      Swal.fire("Error", "Failed to merge leads", "error");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-lg shadow-lg">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">Manage Duplicate Leads</h2>
            <p className="text-sm text-gray-600">
              Found {duplicates.length} leads with duplicate mobile numbers
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-red-500 text-xl font-bold hover:text-red-700"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-700 mb-2">Instructions:</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
              <li>
                For each mobile number, select which lead to{" "}
                <strong>KEEP</strong> (radio button)
              </li>
              <li>
                Select which duplicate leads to <strong>DELETE</strong>{" "}
                (checkboxes)
              </li>
              <li>
                Use "Delete Selected" to remove duplicates while keeping one
                record
              </li>
              <li>
                Use "Merge Selected" to combine data from duplicates into the
                kept lead
              </li>
            </ul>
          </div>

          {/* Duplicates List */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-3">
              All Duplicates by Mobile Number
            </h3>
            {Object.entries(groupedDuplicates).map(([mobile, leads]) => (
              <div
                key={mobile}
                className="mb-6 border border-gray-200 rounded-lg overflow-hidden"
              >
                <div className="bg-gray-100 px-4 py-2 border-b">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <span className="font-medium">Mobile: {mobile}</span>
                      <span className="text-sm text-gray-600">
                        {leads.length} duplicate{leads.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Keep:{" "}
                      <span className="font-medium">
                        Lead #{keepLeadForMobile[mobile] || "Select"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10">
                          Keep
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10">
                          Delete
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          ID
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Email
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Assigned To
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-gray-50">
                          {/* Keep Radio Button */}
                          <td className="px-4 py-2">
                            <input
                              type="radio"
                              name={`keep-${mobile}`}
                              checked={keepLeadForMobile[mobile] === lead.id}
                              onChange={() =>
                                handleKeepLeadChange(mobile, lead.id)
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                          </td>

                          {/* Delete Checkbox */}
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={(
                                selectedLeadsToDelete[mobile] || []
                              ).includes(lead.id)}
                              onChange={(e) =>
                                handleSelectLeadForDeletion(
                                  mobile,
                                  lead.id,
                                  e.target.checked
                                )
                              }
                              disabled={keepLeadForMobile[mobile] === lead.id}
                              className={`h-4 w-4 rounded focus:ring-blue-500 border-gray-300 ${
                                keepLeadForMobile[mobile] === lead.id
                                  ? "opacity-50 cursor-not-allowed"
                                  : "text-blue-600"
                              }`}
                            />
                          </td>

                          {/* Lead Details */}
                          <td className="px-4 py-2 text-sm">#{lead.id}</td>
                          <td className="px-4 py-2 text-sm font-medium">
                            {lead.customerName}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                lead.status === "BOOKED_LEADS" ||
                                lead.status === "COMPLETED"
                                  ? "bg-green-100 text-green-800"
                                  : lead.status === "CANCELLED"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {lead.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">{lead.email}</td>
                          <td className="px-4 py-2 text-sm">
                            {lead.assignedToName || "Unassigned"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {new Date(lead.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 pt-4 border-t flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleMergeSelected}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Merge Selected
            </button>
            <button
              onClick={handleDeleteSelected}
              className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Delete Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main LeadsPage Component
const LeadsPage = () => {
  const api = useAxios();
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [userRole, setUserRole] = useState("");
  const [userId, setUserId] = useState("");

  // Filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssignedTo, setFilterAssignedTo] = useState("");
  const [filterSource, setFilterSource] = useState("");

  const [usersList, setUsersList] = useState([]);
  const [sourceList, setSourceList] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageOptions = [10, 25, 50, 100, 250, 500, 1000];

  // Lead selection for bulk assign
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [assignUserId, setAssignUserId] = useState("");

  // Duplicate detection state
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  const leadStatusOptions = [
    { value: "NEW_LEADS", label: "New Leads" },
    { value: "QUALIFIED_LEADS", label: "Qualified Leads" },
    { value: "DISQUALIFIED_LEADS", label: "Disqualified Leads" },
    { value: "PENDING_LEADS", label: "Pending Leads" },
    { value: "INTERESTED_LEADS", label: "Interested Leads" },
    { value: "NOT_INTERESTED_LEADS", label: "Not Interested" },
    { value: "BUY_LATER", label: "Buy Later" },
    { value: "INVALID_NUMBER", label: "Invalid Number" },
    { value: "NOT_PICKED_LEADS", label: "Not Picked" },

    { value: "DEMO_SCHEDULED_LEADS", label: "Demo Scheduled" },
    { value: "DEMO_CONDUCTED", label: "Demo Conducted" },
    { value: "DEMO_RESCHEDULED", label: "Demo Rescheduled" },

    { value: "CALL_SCHEDULED_LEADS", label: "Call Scheduled" },
    { value: "VISIT_SCHEDULED_LEADS", label: "Visit Scheduled" },
    { value: "VISIT_DONE_LEADS", label: "Visit Done" },

    { value: "SALE_DONE", label: "Sale Done" },
    {
      value: "SALE_DONE_PAYMENT_PENDING",
      label: "Sale Done – Payment Pending",
    },
    { value: "PAYMENT_PENDING", label: "Payment Pending" },

    { value: "RETENTION_CLIENTS", label: "Retention Client" },
    { value: "OTHERS", label: "Others" },
  ];

  useEffect(() => {
    const role = localStorage.getItem("role");
    const id = localStorage.getItem("userId");
    setUserRole(role || "");
    setUserId(id || "");

    if (role !== "ADMIN") {
      setActiveTab("assigned");
    }

    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const usersRes = await api.get("/auth/id-names");
      const sourceRes = await api.get("/api/sources/get-all-sources");

      setUsersList(usersRes.data.data || []);
      setSourceList(sourceRes.data || []);
    } catch (error) {
      console.log("Filter fetch error:", error);
    }
  };

  const getAllLeads = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/leads");
      setLeads(res.data.data);
    } catch (err) {
      Swal.fire("Error", "Failed to load leads", "error");
    } finally {
      setLoading(false);
    }
  };

  const getLeadsAssignedToMe = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/leads/assigned-to/${userId}`);
      setLeads(res.data.data);
    } catch (err) {
      Swal.fire("Error", "Failed to load assigned leads", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    if (activeTab === "all") await getAllLeads();
    else await getLeadsAssignedToMe();
  };

  useEffect(() => {
    if (userRole) loadLeads();
  }, [activeTab, userRole]);

  useEffect(() => {
    // Reset to first page when items per page changes
    setCurrentPage(1);
  }, [itemsPerPage]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch("");
    setCurrentPage(1);
  };

  const handleDeleteLead = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Lead will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/api/leads/${id}`);
      Swal.fire("Deleted!", "Lead deleted successfully!", "success");
      loadLeads();
    } catch (err) {
      Swal.fire("Error", "Failed to delete lead", "error");
    }
  };

  /** Bulk Upload CSV */
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);

      const res = await api.post("/api/leads/bulk", formData);
      Swal.fire(
        "Success",
        res.data.message || "Leads uploaded successfully!",
        "success"
      );
      loadLeads();
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to upload leads",
        "error"
      );
    } finally {
      setLoading(false);
      e.target.value = null;
    }
  };

  /** Handle checkbox selection */
  const toggleSelectLead = (leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  /** Assign single lead */
  const handleAssignSingleLead = async (leadId) => {
    if (!assignUserId) {
      Swal.fire("Error", "Please select a user to assign", "error");
      return;
    }

    try {
      setLoading(true);
      await api.put(`/api/leads/${leadId}/assigned-to`, {
        assignedToId: assignUserId,
      });
      Swal.fire("Success", "Lead assigned successfully!", "success");
      loadLeads();
    } catch (err) {
      Swal.fire("Error", "Failed to assign lead", "error");
    } finally {
      setLoading(false);
    }
  };

  /** Bulk assign selected leads */
  const handleBulkAssignLeads = async () => {
    if (!assignUserId || selectedLeads.length === 0) {
      Swal.fire("Error", "Please select leads and user", "error");
      return;
    }

    try {
      setLoading(true);
      await api.post("/api/leads/assign/bulk", {
        leadIds: selectedLeads,
        assignedToId: assignUserId,
      });
      Swal.fire("Success", "Leads assigned successfully!", "success");
      setSelectedLeads([]);
      loadLeads();
    } catch (err) {
      Swal.fire("Error", "Failed to assign leads", "error");
    } finally {
      setLoading(false);
    }
  };

  /** Find Duplicates by Mobile Number */
  const findDuplicatesByMobile = () => {
    setCheckingDuplicates(true);

    // Find leads with duplicate mobile numbers
    const mobileCount = {};
    leads.forEach((lead) => {
      if (lead.mobile) {
        mobileCount[lead.mobile] = (mobileCount[lead.mobile] || 0) + 1;
      }
    });

    const duplicateMobiles = Object.keys(mobileCount).filter(
      (mobile) => mobileCount[mobile] > 1
    );

    if (duplicateMobiles.length === 0) {
      Swal.fire(
        "No Duplicates",
        "No duplicate mobile numbers found in the current leads list.",
        "info"
      );
      setCheckingDuplicates(false);
      return;
    }

    // Get all leads with duplicate mobile numbers
    const duplicateLeads = leads.filter(
      (lead) => lead.mobile && duplicateMobiles.includes(lead.mobile)
    );

    setDuplicates(duplicateLeads);
    setShowDuplicatesModal(true);
    setCheckingDuplicates(false);
  };

  /** Merge duplicate leads */
  const handleMergeLeads = async (leadIds, mergeIntoLeadId) => {
    try {
      const response = await api.post("/api/leads/merge", {
        leadIds,
        mergeIntoLeadId,
      });

      // Reload leads after merge
      loadLeads();
      Swal.fire("Success", "Leads merged successfully!", "success");
      return response.data;
    } catch (error) {
      Swal.fire("Error", "Failed to merge leads", "error");
      throw error;
    }
  };

  /** Delete duplicate leads */
  const handleDeleteLeads = async (leadIds) => {
    try {
      // Delete leads one by one
      for (const leadId of leadIds) {
        await api.delete(`/api/leads/${leadId}`);
      }

      // Reload leads after deletion
      loadLeads();
      Swal.fire("Success", "Leads deleted successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to delete leads", "error");
      throw error;
    }
  };

  /** Close Duplicates Modal */
  const closeDuplicatesModal = () => {
    setShowDuplicatesModal(false);
    setDuplicates([]);
  };

  /** Apply filters */
  const filteredLeads = leads
    .filter((lead) => {
      const cleanName = (lead.customerName || "").replace(/[^a-zA-Z ]/g, "");
      const cleanSearch = (search || "").replace(/[^a-zA-Z ]/g, "");

      return cleanName.toLowerCase().includes(cleanSearch.toLowerCase());
    })
    .filter((lead) => (filterStatus ? lead.status === filterStatus : true))
    .filter((lead) =>
      filterAssignedTo ? lead.assignedToId === filterAssignedTo : true
    )
    .filter((lead) => (filterSource ? lead.sourceName === filterSource : true));

  /** Pagination */
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const displayedLeads = filteredLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate serial number for each lead based on current page
  const getSerialNumber = (index) => {
    return (currentPage - 1) * itemsPerPage + index + 1;
  };

  // Pagination button generation with responsive design
  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5; // Adjust based on screen size

    if (totalPages <= maxVisibleButtons) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(i);
      }
    } else {
      // Always show first page
      buttons.push(1);

      // Calculate start and end of pagination buttons
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if at the beginning
      if (currentPage <= 3) {
        end = Math.min(totalPages - 1, maxVisibleButtons - 1);
      }

      // Adjust if at the end
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - (maxVisibleButtons - 2));
      }

      // Add ellipsis after first page if needed
      if (start > 2) {
        buttons.push("...");
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        buttons.push(i);
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        buttons.push("...");
      }

      // Always show last page if there's more than 1 page
      if (totalPages > 1) {
        buttons.push(totalPages);
      }
    }

    return buttons;
  };

  // Check if user is admin
  const isAdmin = userRole === "ADMIN";

  return (
    <div className="p-4 md:p-6">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-blue-700">
            Lead Management
          </h1>
          <p className="text-sm text-gray-600">
            <a href="/dashboard" className="text-blue-600 hover:underline">
              Dashboard
            </a>{" "}
            / <span className="font-semibold text-blue-700">Leads</span>
          </p>
        </div>

        {/* Search */}
        <div className="w-full md:w-auto">
          <div className="flex items-center border rounded-lg overflow-hidden w-full md:w-64">
            <input
              type="text"
              placeholder="Search leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 outline-none flex-1"
            />
          </div>
        </div>
      </div>

      {/* TABS - Only show for ADMIN */}
      {isAdmin && (
        <div className="mb-6 border-b">
          <div className="flex space-x-1 overflow-x-auto">
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === "all"
                  ? "bg-blue-700 text-white border-b-2 border-blue-700"
                  : "text-gray-600 hover:text-blue-700 hover:bg-blue-50"
              }`}
              onClick={() => handleTabChange("all")}
            >
              All Leads
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === "assigned"
                  ? "bg-blue-700 text-white border-b-2 border-blue-700"
                  : "text-gray-600 hover:text-blue-700 hover:bg-blue-50"
              }`}
              onClick={() => handleTabChange("assigned")}
            >
              Leads Assigned to Me
            </button>
          </div>
        </div>
      )}

      {/* ACTION BUTTONS - Bulk Assign section only for ADMIN */}
      {isAdmin && (
        <div className="mt-4 flex flex-col md:flex-row justify-between gap-3 pb-5 items-start md:items-center">
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <select
              className="border p-2 rounded w-full md:w-40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
            >
              <option value="">Assign To</option>
              {usersList.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </select>
            <button
              className="px-4 py-2 bg-purple-700 text-white rounded hover:bg-purple-800 whitespace-nowrap transition-colors"
              onClick={handleBulkAssignLeads}
            >
              Assign Selected Leads
            </button>
            <button
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 whitespace-nowrap transition-colors flex items-center gap-2"
              onClick={findDuplicatesByMobile}
              disabled={checkingDuplicates}
            >
              <DocumentDuplicateIcon className="w-4 h-4" />
              {checkingDuplicates ? "Checking..." : "Find Duplicates"}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto mt-3 md:mt-0">
            <button
              onClick={() => navigate("/leads/add")}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 whitespace-nowrap text-center transition-colors"
            >
              Add Lead
            </button>
            <label className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer whitespace-nowrap text-center transition-colors">
              Upload CSV
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleBulkUpload}
              />
            </label>
          </div>
        </div>
      )}

      {/* Action buttons for non-admin users */}
      {!isAdmin && (
        <div className="mt-4 pb-5">
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <button
              onClick={() => navigate("/leads/add")}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 whitespace-nowrap text-center transition-colors"
            >
              Add Lead
            </button>
            <button
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 whitespace-nowrap transition-colors flex items-center gap-2"
              onClick={findDuplicatesByMobile}
              disabled={checkingDuplicates}
            >
              <DocumentDuplicateIcon className="w-4 h-4" />
              {checkingDuplicates ? "Checking..." : "Find Duplicates"}
            </button>
          </div>
        </div>
      )}

      {/* FILTERS - Show all filters for ADMIN, only status and source for non-admin */}
      <div
        className={`grid ${
          isAdmin ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"
        } gap-4 pb-5`}
      >
        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Filter by Status
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Status</option>
            {leadStatusOptions.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {isAdmin && (
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Filter by Assigned To
            </label>
            <select
              value={filterAssignedTo}
              onChange={(e) => setFilterAssignedTo(e.target.value)}
              className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Employees</option>
              {usersList.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Filter by Source
          </label>
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Sources</option>
            {sourceList.map((s) => (
              <option key={s.id} value={s.sourceName}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Pagination Controls - Top */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
        <div className="text-sm text-gray-600">
          Showing{" "}
          <span className="font-semibold">
            {(currentPage - 1) * itemsPerPage + 1}
          </span>{" "}
          to{" "}
          <span className="font-semibold">
            {Math.min(currentPage * itemsPerPage, filteredLeads.length)}
          </span>{" "}
          of <span className="font-semibold">{filteredLeads.length}</span> leads
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 whitespace-nowrap">
            Leads per page:
          </span>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="border rounded p-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto bg-white shadow-lg rounded-lg">
        <table className="min-w-full text-left">
          <thead>
            <tr className="bg-blue-100 text-blue-900">
              {isAdmin && (
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked)
                        setSelectedLeads(displayedLeads.map((l) => l.id));
                      else setSelectedLeads([]);
                    }}
                    checked={
                      displayedLeads.every((l) =>
                        selectedLeads.includes(l.id)
                      ) && displayedLeads.length > 0
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
              )}
              <th className="px-4 py-3 font-medium">S.No</th>
              <th className="px-4 py-3 font-medium">Customer Name</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Mobile</th>
              <th className="px-4 py-3 font-medium">Email</th>

              {isAdmin && activeTab === "all" && (
                <th className="px-4 py-3 font-medium">Assigned To</th>
              )}
              <th className="px-4 py-3 font-medium text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={isAdmin ? (activeTab === "all" ? 8 : 7) : 6}
                  className="text-center py-6"
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700 mb-2"></div>
                    <p className="text-gray-600">Loading leads...</p>
                  </div>
                </td>
              </tr>
            ) : displayedLeads.length === 0 ? (
              <tr>
                <td
                  colSpan={isAdmin ? (activeTab === "all" ? 8 : 7) : 6}
                  className="text-center py-8 text-gray-500"
                >
                  <ClockIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p>No leads found</p>
                </td>
              </tr>
            ) : (
              displayedLeads.map((lead, index) => (
                <tr
                  key={lead.id}
                  className="border-b hover:bg-gray-50 transition-colors"
                >
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => toggleSelectLead(lead.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium">
                    {getSerialNumber(index)}
                  </td>
                  {/* Clickable Customer Name for View Page */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/leads/view/${lead.id}`)}
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
                    >
                      {lead.customerName}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.status === "BOOKED_LEADS" ||
                        lead.status === "COMPLETED"
                          ? "bg-green-100 text-green-800"
                          : lead.status === "CANCELLED"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {leadStatusOptions.find((s) => s.value === lead.status)
                        ?.label || lead.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">{lead.mobile}</td>
                  <td className="px-4 py-3">{lead.email}</td>

                  {isAdmin && activeTab === "all" && (
                    <td className="px-4 py-3">
                      {lead.assignedToName || "Unassigned"}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <button
                        className="text-yellow-600 hover:text-yellow-800 p-1 hover:bg-yellow-50 rounded transition-colors"
                        onClick={() => navigate(`/leads/edit/${lead.id}`)}
                        title="Edit"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors"
                        onClick={() => handleDeleteLead(lead.id)}
                        title="Delete"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                      {isAdmin && (
                        <button
                          className="text-green-700 hover:text-green-800 px-2 py-1 rounded text-xs border border-green-700 hover:bg-green-50 transition-colors"
                          onClick={() => handleAssignSingleLead(lead.id)}
                          title="Assign"
                        >
                          Assign
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION - Bottom */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
          <div className="text-sm text-gray-600">
            Page <span className="font-semibold">{currentPage}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
          </div>

          <div className="flex items-center gap-1 overflow-x-auto py-2 max-w-full">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded flex items-center gap-1 transition-colors ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <ChevronLeftIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            <div className="flex items-center gap-1">
              {getPaginationButtons().map((page, index) => (
                <button
                  key={index}
                  onClick={() => page !== "..." && setCurrentPage(page)}
                  disabled={page === "..." || page === currentPage}
                  className={`min-w-[2.5rem] px-2 py-2 rounded text-sm transition-colors ${
                    page === "..."
                      ? "bg-transparent text-gray-500 cursor-default"
                      : page === currentPage
                      ? "bg-blue-700 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded flex items-center gap-1 transition-colors ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 whitespace-nowrap">
              Go to page:
            </span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const value = Math.max(
                  1,
                  Math.min(Number(e.target.value), totalPages)
                );
                setCurrentPage(value);
              }}
              className="border rounded p-1 w-16 text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {/* Duplicates Modal */}
      {showDuplicatesModal && (
        <DuplicatesModal
          duplicates={duplicates}
          onClose={closeDuplicatesModal}
          onMerge={handleMergeLeads}
          onDelete={handleDeleteLeads}
        />
      )}
    </div>
  );
};

export default LeadsPage;
